import { useEffect, useState } from 'react';
import { Alert, Button, Card, SectionHeading, Spinner } from './ui.js';
import { Mascot } from './Mascot.js';
import { INTEREST, helpApi, type GrowthPreferences } from './helpGrowthApi.js';

/**
 * Grow with ROI Pros: a permanent, quiet place, never a popup.
 *
 * A customer picks what they care about, reads what ROI Pros does about it, and may ask
 * for a consultation. Booking uses a page ROI Pros has verified when one is configured,
 * and otherwise a request form. Nothing here reads or sends a contact the customer
 * captured, and marketing consent is a separate, unticked choice.
 */
export function GrowWithRoiPros({ initialStep = 'interests' }: { initialStep?: 'interests' | 'consult' }) {
  const [prefs, setPrefs] = useState<GrowthPreferences | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [step, setStep] = useState<'interests' | 'explore' | 'consult' | 'done'>(initialStep);
  const [form, setForm] = useState({ contactName: '', contactEmail: '', contactPhone: '', company: '', preferredTime: '' });
  const [consentContact, setConsentContact] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { helpApi.preferences().then((p) => { setPrefs(p); setPicked(p.interests); }).catch(() => undefined); }, []);
  if (!prefs) return <Spinner />;

  const toggle = (k: string) => setPicked((x) => (x.includes(k) ? x.filter((y) => y !== k) : [...x, k]));
  const saveInterests = async () => { setPrefs(await helpApi.savePreferences({ interests: picked })); };
  const input = 'mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-2 text-base sm:text-sm';

  const request = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await helpApi.requestConsultation({
        interests: picked, contactName: form.contactName, contactEmail: form.contactEmail,
        ...(form.contactPhone ? { contactPhone: form.contactPhone } : {}), ...(form.company ? { company: form.company } : {}),
        ...(form.preferredTime ? { preferredTime: form.preferredTime } : {}), consentContact: true, consentMarketing,
      });
      if (r.routed) { setStep('done'); return; }
      setNotice(r.status === 'received'
        ? { tone: 'info', text: 'Saved, but not sent: consultation requests cannot be delivered from here yet. Please contact ROI Pros directly.' }
        : { tone: 'error', text: 'Saved, but it could not be delivered just now. Please try again later.' });
    } catch (e) {
      setNotice({ tone: 'error', text: (e as Error).message || 'That could not be sent.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-grow="">
      <div className="flex items-center gap-3">
        <Mascot pose="idea" size={72} />
        <SectionHeading title="Grow with ROI Pros" hint="Maximize your time, team and money. Pick what matters to you." />
      </div>
      <Card className="space-y-4 p-4 sm:p-6">
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">What would help most right now?</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {Object.entries(INTEREST).map(([k, v]) => (
              <label key={k} className={`flex min-h-11 cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${picked.includes(k) ? 'border-cp-blue-600 bg-cp-blue-50' : 'border-slate-200'}`}>
                <input type="checkbox" className="mt-0.5" checked={picked.includes(k)} onChange={() => toggle(k)} />
                <span><span className="block font-medium text-slate-900">{v.title}</span><span className="block text-xs text-slate-600">{v.blurb}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button className="min-h-11" disabled={picked.length === 0} onClick={() => { void saveInterests(); setStep('explore'); }}>Explore My Options</Button>
          <Button className="min-h-11" variant="secondary" disabled={picked.length === 0} onClick={() => { void saveInterests(); setStep('consult'); }}>Book a Consultation</Button>
        </div>
      </Card>

      {step === 'explore' ? (
        <Card className="space-y-2 p-4 sm:p-6" data-grow-explore="">
          <h3 className="text-base font-semibold text-slate-900">How ROI Pros helps with what you picked</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            {picked.map((k) => <li key={k}><span className="font-medium">{INTEREST[k]?.title}:</span> {INTEREST[k]?.blurb}</li>)}
          </ul>
          <p className="text-sm text-slate-600">ROI Pros builds these on LevelUp, the platform ContactPilot is part of. A short consultation is the fastest way to see what fits.</p>
          <Button className="min-h-11" variant="secondary" onClick={() => setStep('consult')}>Book a Consultation</Button>
        </Card>
      ) : null}

      {step === 'consult' ? (
        prefs.bookingUrl ? (
          <Card className="space-y-2 p-4 sm:p-6">
            <p className="text-sm text-slate-700">Choose a time that suits you on the ROI Pros booking page.</p>
            <a className="inline-flex min-h-11 items-center rounded-md bg-cp-blue-700 px-4 text-sm font-semibold text-white" href={prefs.bookingUrl} target="_blank" rel="noopener noreferrer">Open the booking page</a>
          </Card>
        ) : (
          <Card className="space-y-3 p-4 sm:p-6" data-grow-consult="">
            <h3 className="text-base font-semibold text-slate-900">Request a consultation</h3>
            {!prefs.routingAvailable ? <Alert tone="info">Requests can be written here, but cannot be delivered from here yet.</Alert> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {([['contactName', 'Your name'], ['contactEmail', 'Email'], ['contactPhone', 'Phone (optional)'], ['company', 'Company (optional)']] as const).map(([k, label]) => (
                <label key={k} className="block min-w-0 text-sm"><span className="text-slate-600">{label}</span>
                  <input className={input} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} type={k === 'contactEmail' ? 'email' : 'text'} /></label>
              ))}
            </div>
            <label className="block text-sm"><span className="text-slate-600">When suits you? (optional)</span>
              <input className={input} value={form.preferredTime} onChange={(e) => setForm((f) => ({ ...f, preferredTime: e.target.value }))} placeholder="e.g. weekday mornings, Eastern time" /></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={consentContact} onChange={(e) => setConsentContact(e.target.checked)} />
              <span>ROI Pros may contact me about this request.</span></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={consentMarketing} onChange={(e) => setConsentMarketing(e.target.checked)} />
              <span>Also send me occasional tips and offers from ROI Pros. (Optional; you can stop any time.)</span></label>
            {notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : null}
            <Button className="min-h-11" disabled={busy || !consentContact || !form.contactName.trim() || !/.+@.+\..+/.test(form.contactEmail)} onClick={() => void request()}>
              {busy ? 'Sending…' : 'Request a consultation'}
            </Button>
          </Card>
        )
      ) : null}

      {step === 'done' ? <div className="flex justify-center"><Mascot pose="success" size={96} /></div> : null}
      {step === 'done' ? <Alert tone="success">Thanks. ROI Pros received your request and will contact you to find a time.</Alert> : null}

      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" className="mt-0.5" checked={prefs.promotionsOptOut}
          onChange={async (e) => setPrefs(await helpApi.savePreferences({ promotionsOptOut: e.target.checked }))} />
        <span>Don&rsquo;t show me ROI Pros suggestions inside ContactPilot. This page stays available in the menu.</span>
      </label>
    </div>
  );
}

/** Shown once on the Dashboard after something worked; never during onboarding; dismissible for good. */
export function GrowthDiscovery({ onOpen }: { onOpen: (step: 'interests' | 'consult') => void }) {
  const [prefs, setPrefs] = useState<GrowthPreferences | null>(null);
  useEffect(() => { helpApi.preferences().then(setPrefs).catch(() => undefined); }, []);
  if (!prefs?.showDiscovery) return null;
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-growth-discovery="">
      <Mascot pose="celebrate" size={56} className="hidden sm:block" />
      <div className="min-w-0 sm:flex-1">
        <p className="text-sm font-semibold text-slate-900">Your first contacts are through. Want more of them?</p>
        <p className="text-xs text-slate-600">ROI Pros helps with leads, follow-up, automation and growth.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button className="min-h-11" variant="secondary" onClick={() => onOpen('interests')}>Explore My Options</Button>
        <Button className="min-h-11" variant="secondary" onClick={() => onOpen('consult')}>Book a Consultation</Button>
        <Button className="min-h-11" variant="secondary" onClick={async () => { await helpApi.savePreferences({ discoveryDismissed: true }); setPrefs(null); }}>Maybe Later</Button>
      </div>
    </Card>
  );
}
